import { isCompNode, type CompNode, type Sheet } from './store';
import { computeNets } from './nets';

export type RuleSeverity = 'error' | 'warning' | 'info';

export interface RuleIssue {
  id: string;
  severity: RuleSeverity;
  /** short rule code, e.g. "unconnected-pin" */
  rule: string;
  message: string;
  sheetId?: string;
  sheetName?: string;
  /** navigation targets — an edge takes priority over a node */
  nodeId?: string;
  edgeId?: string;
}

function refKey(component: string, connector: string) {
  return `${component}||${connector}`;
}

/**
 * Design-rule check across the whole project. Pure: takes the sheet list and
 * returns a flat issue list (no store access), so it can run from a button or
 * a test. Severity tiers:
 *   error   — structurally broken (self-loop wire, dangling harness segment)
 *   warning — likely a mistake (floating pin on a wired part, empty harness)
 *   info    — completeness nits (missing gauge / part number / termination)
 */
export function runRuleCheck(sheets: Sheet[]): RuleIssue[] {
  const issues: RuleIssue[] = [];
  let seq = 0;
  const add = (i: Omit<RuleIssue, 'id'>) => issues.push({ id: `rc${seq++}`, ...i });

  // ---- harness rollup (built while scanning schematic edges) ----
  const harnessWireCount = new Map<string, number>();
  const harnessEndpointKeys = new Map<string, Set<string>>();
  const recordHarnessEnd = (hid: string, key: string) => {
    let set = harnessEndpointKeys.get(hid);
    if (!set) {
      set = new Set();
      harnessEndpointKeys.set(hid, set);
    }
    set.add(key);
  };

  for (const sh of sheets) {
    if (sh.kind !== 'schematic') continue;

    const comps = new Map<string, CompNode>();
    for (const n of sh.nodes) {
      if (isCompNode(n)) comps.set(n.id, n);
    }

    // pin usage: `${nodeId}|${pinId}` -> count
    const pinUse = new Map<string, number>();
    const bump = (k: string) => pinUse.set(k, (pinUse.get(k) ?? 0) + 1);

    for (const e of sh.edges) {
      if (e.source && e.sourceHandle) bump(`${e.source}|${e.sourceHandle}`);
      if (e.target && e.targetHandle) bump(`${e.target}|${e.targetHandle}`);

      const src = e.source ? comps.get(e.source) : null;
      const tgt = e.target ? comps.get(e.target) : null;
      const label = e.data?.label || src?.data.label || 'wire';

      if (e.source && e.source === e.target) {
        add({
          severity: 'error',
          rule: 'self-loop',
          message: `Wire on "${src?.data.label ?? '?'}" connects the component to itself`,
          sheetId: sh.id,
          sheetName: sh.name,
          edgeId: e.id,
        });
      }

      if (!e.data?.gauge) {
        add({
          severity: 'info',
          rule: 'wire-spec',
          message: `Wire "${label}" has no gauge set`,
          sheetId: sh.id,
          sheetName: sh.name,
          edgeId: e.id,
        });
      }

      const hid = e.data?.harnessId;
      if (hid) {
        harnessWireCount.set(hid, (harnessWireCount.get(hid) ?? 0) + 1);
        const sp = src?.data.pins.find((p) => p.id === e.sourceHandle);
        const tp = tgt?.data.pins.find((p) => p.id === e.targetHandle);
        if (src) recordHarnessEnd(hid, refKey(src.data.label, sp?.group ?? ''));
        if (tgt) recordHarnessEnd(hid, refKey(tgt.data.label, tp?.group ?? ''));
      }
    }

    // Floating pins — only flagged on components that are PARTIALLY wired
    // (≥1 connected pin). A fully-unconnected part is usually just not placed
    // yet, so flagging all its pins would drown the report in noise.
    for (const c of comps.values()) {
      const connected = c.data.pins.filter((p) => pinUse.has(`${c.id}|${p.id}`)).length;
      if (connected === 0 || connected === c.data.pins.length) continue;
      for (const p of c.data.pins) {
        if (!pinUse.has(`${c.id}|${p.id}`)) {
          add({
            severity: 'warning',
            rule: 'unconnected-pin',
            message: `${c.data.label}: pin ${p.group ? `${p.group}.` : ''}${p.name} is unconnected`,
            sheetId: sh.id,
            sheetName: sh.name,
            nodeId: c.id,
          });
        }
      }
    }

    // BOM completeness — components without a part number.
    for (const c of comps.values()) {
      if (!c.data.partNumber) {
        add({
          severity: 'info',
          rule: 'no-part-number',
          message: `${c.data.label} has no part number`,
          sheetId: sh.id,
          sheetName: sh.name,
          nodeId: c.id,
        });
      }
    }
  }

  // ---- interconnect bundles ----
  for (const sh of sheets) {
    if (sh.kind !== 'interconnect') continue;
    for (const b of sh.bundles ?? []) {
      const wc = harnessWireCount.get(b.id) ?? 0;
      if (wc === 0) {
        add({
          severity: 'warning',
          rule: 'empty-harness',
          message: `Harness "${b.name}" has no wires assigned`,
          sheetId: sh.id,
          sheetName: sh.name,
        });
      }
      const g = b.graph;
      const nodeIds = new Set((g?.nodes ?? []).map((n) => n.id));
      for (const seg of g?.segments ?? []) {
        if (!nodeIds.has(seg.from) || !nodeIds.has(seg.to)) {
          add({
            severity: 'error',
            rule: 'dangling-segment',
            message: `Harness "${b.name}" has a segment with a missing endpoint`,
            sheetId: sh.id,
            sheetName: sh.name,
          });
        }
      }
      // Connector metadata nodes are only materialized once a user edits
      // termination/contact PN, so check against the live wired endpoints
      // (not `g.nodes`, which is sparse) or a fully-wired-but-untouched
      // connector would never get flagged.
      const epKeys = harnessEndpointKeys.get(b.id) ?? new Set<string>();
      const terminatedKeys = new Set(
        (g?.nodes ?? [])
          .filter((n) => n.type === 'connector' && n.termination)
          .map((n) => refKey(n.refComponent ?? '', n.refConnector ?? '')),
      );
      for (const key of epKeys) {
        if (terminatedKeys.has(key)) continue;
        const [component, connector] = key.split('||');
        add({
          severity: 'info',
          rule: 'no-termination',
          message: `Harness "${b.name}": connector "${connector || component}" (${component}) has no termination specified`,
          sheetId: sh.id,
          sheetName: sh.name,
        });
      }
    }
  }

  // ---- net-aware ERC ----
  const sheetNameById = new Map(sheets.map((s) => [s.id, s.name]));
  for (const net of computeNets(sheets)) {
    const sheetId = net.sheetIds[0];
    const sheetNm = sheetId ? sheetNameById.get(sheetId) : undefined;
    const edgeId = net.edgeIds[0];
    if (net.labelNames.length > 1) {
      add({
        severity: 'error',
        rule: 'net-name-conflict',
        message: `Net naming conflict: ${net.labelNames.join(' / ')} are tied to the same net`,
        sheetId,
        sheetName: sheetNm,
        edgeId,
      });
    }
    if (net.terminalCount < 2) {
      add({
        severity: 'warning',
        rule: 'single-connection-net',
        message: `Net "${net.name}" has only one connection`,
        sheetId,
        sheetName: sheetNm,
        edgeId,
      });
    }
  }

  // stable ordering: error → warning → info
  const order: Record<RuleSeverity, number> = { error: 0, warning: 1, info: 2 };
  issues.sort((a, b) => order[a.severity] - order[b.severity]);
  return issues;
}
